import { useState, type ReactNode } from "react"
import { AlertCircle, CheckCircle2, Info, X } from "lucide-react"
import { cn } from "../../lib/cn"

export type AlertVariant = "info" | "success" | "danger" | "warning"

const variantConfig: Record<AlertVariant, { classes: string; icon: ReactNode }> = {
  info: { classes: "bg-accent/10 text-accent", icon: <Info className="size-5" /> },
  success: { classes: "bg-success/10 text-success", icon: <CheckCircle2 className="size-5" /> },
  danger: { classes: "bg-danger/10 text-danger", icon: <AlertCircle className="size-5" /> },
  warning: { classes: "bg-accent/10 text-accent", icon: <AlertCircle className="size-5" /> },
}

export interface AlertProps {
  variant?: AlertVariant
  title?: string
  children: ReactNode
  dismissible?: boolean
  onDismiss?: () => void
  className?: string
}

export function Alert({ variant = "info", title, children, dismissible = false, onDismiss, className }: AlertProps) {
  const [dismissed, setDismissed] = useState(false)
  if (dismissed) return null

  const config = variantConfig[variant]

  return (
    <div
      role="alert"
      className={cn(
        "flex items-start gap-sm rounded-box p-md text-body-sm",
        config.classes,
        className,
      )}
    >
      <span className="mt-2xs shrink-0">{config.icon}</span>
      <div className="flex-1">
        {title && <p className="mb-2xs font-semibold">{title}</p>}
        <div>{children}</div>
      </div>
      {dismissible && (
        <button
          type="button"
          aria-label="إغلاق"
          onClick={() => {
            setDismissed(true)
            onDismiss?.()
          }}
          className="shrink-0 rounded-button p-2xs hover:bg-black/5"
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  )
}
